// src/services/userService.ts
import apiClient from './api'
import type { User, PayoutMethod, PayoutInstruction, Purchase, PayoutBalance, OwnReview, PublicSellerReview, SavedSearch, SavedSearchCriteria } from '@/types'

export interface PageMeta {
  currentPage: number
  lastPage: number
  perPage: number
  total: number
}

// Deleting one's own account: what still has to finish, or what goes and
// what stays (server accountErasureController).
export interface OwnErasurePreview {
  blockers: Array<{ key: string; count: number }>
  erase: Record<string, number>
  keep: Record<string, number>
}

export const userService = {
  async getProfile(): Promise<{ success: boolean; data: User }> {
    const res = await apiClient.get<{ success: boolean; data: User }>('/api/user/profile')
    return res.data
  },

  async updateProfile(data: Partial<User>): Promise<{ success: boolean; data: User }> {
    const res = await apiClient.put<{ success: boolean; data: User }>('/api/user/profile', data)
    return res.data
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message?: string }> {
    const res = await apiClient.put<{ success: boolean; message?: string }>('/api/user/password', {
      currentPassword,
      newPassword,
    })
    return res.data
  },

  async getListings(status?: string): Promise<{ success: boolean; data: any[] }> {
    const res = await apiClient.get<{ success: boolean; data: any[] }>('/api/user/listings', { status })
    return res.data
  },

  async getBids(page = 1, perPage = 20): Promise<{ success: boolean; data: any[]; meta?: PageMeta }> {
    const res = await apiClient.get<{ success: boolean; data: any[]; meta?: PageMeta }>('/api/user/bids', { page, perPage })
    return res.data
  },

  async getWatchlist(): Promise<{ success: boolean; data: any[] }> {
    const res = await apiClient.get<{ success: boolean; data: any[] }>('/api/user/watchlist')
    return res.data
  },

  // Sends the desired state, not a blind toggle, so a retried request can't
  // flip the heart back.
  async toggleWatchlist(auctionId: string, watch?: boolean): Promise<{ success: boolean; isWatching: boolean }> {
    const res = await apiClient.post<{ success: boolean; isWatching: boolean }>(
      `/api/user/watchlist/${auctionId}`,
      typeof watch === 'boolean' ? { watch } : undefined
    )
    return res.data
  },

  async getOwnErasurePreview(): Promise<OwnErasurePreview> {
    const res = await apiClient.get<{ success: boolean; data: OwnErasurePreview }>('/api/user/account/erasure')
    if (!res.data?.success) throw new Error('preview failed')
    return res.data.data
  },

  // Everything the platform keeps about the account, as one object.
  async exportOwnData(): Promise<unknown> {
    const res = await apiClient.get<{ success: boolean; data: unknown }>('/api/user/account/export')
    if (!res.data?.success) throw new Error('export failed')
    return res.data.data
  },

  // Irreversible. Throws with the server's message (wrong password, open deals).
  async eraseOwnAccount(password: string): Promise<void> {
    const res = await apiClient.post<{ success: boolean; error?: string }>('/api/user/account/erase', { password })
    if (!res.data?.success) throw new Error(res.data?.error || '')
  },

  // The reviewed seller's public answer (or an edit of it within 30 days).
  async replyToReview(reviewId: string, text: string): Promise<{ reply: string; replyAt: string | null }> {
    const res = await apiClient.put<{ success: boolean; data: { reply: string; replyAt: string | null } }>(`/api/user/reviews/${reviewId}/reply`, { text })
    if (!res.data?.success) throw new Error('reply failed')
    return res.data.data
  },

  async getSavedSearches(): Promise<{ success: boolean; data: SavedSearch[] }> {
    const res = await apiClient.get<{ success: boolean; data: SavedSearch[] }>('/api/user/saved-searches')
    return res.data
  },

  // created is false when this exact filter was already saved.
  async saveSearch(criteria: SavedSearchCriteria): Promise<{ success: boolean; data: SavedSearch; created: boolean }> {
    const res = await apiClient.post<{ success: boolean; data: SavedSearch; created: boolean }>('/api/user/saved-searches', criteria)
    return res.data
  },

  async setSavedSearchEmail(id: string, emailEnabled: boolean): Promise<{ success: boolean; data: SavedSearch }> {
    const res = await apiClient.put<{ success: boolean; data: SavedSearch }>(`/api/user/saved-searches/${id}`, { emailEnabled })
    return res.data
  },

  async deleteSavedSearch(id: string): Promise<{ success: boolean }> {
    const res = await apiClient.delete<{ success: boolean }>(`/api/user/saved-searches/${id}`)
    return res.data
  },

  async getKyc(): Promise<{ success: boolean; data: any }> {
    const res = await apiClient.get<{ success: boolean; data: any }>('/api/user/kyc')
    return res.data
  },

  async submitKyc(data: {
    inn?: string
    idFrontUrl?: string
    idBackUrl?: string
    selfieUrl?: string
    proofOfAddressUrl?: string
    faceConsent?: boolean
  }): Promise<{ success: boolean; data: any }> {
    const res = await apiClient.post<{ success: boolean; data: any }>('/api/user/kyc', data)
    return res.data
  },

  async uploadKycDocument(file: File, type: string): Promise<{ success: boolean; url: string }> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)
    const res = await apiClient.post<{ success: boolean; url: string }>('/api/upload/kyc', formData)
    return res.data
  },

  // Live selfie: a random head-turn sequence, then the captured frames
  // (straight first, then one per turn). Pass -> the selfie URL to submit.
  async startLivenessChallenge(): Promise<{ challengeId: string; steps: Array<'left' | 'right'>; expiresInSec: number }> {
    const res = await apiClient.post<{ success: boolean; data: { challengeId: string; steps: Array<'left' | 'right'>; expiresInSec: number } }>('/api/user/kyc/liveness/challenge', {})
    return res.data.data
  },

  async submitLivenessFrames(challengeId: string, frames: Blob[]): Promise<{ status: string; selfieUrl: string }> {
    const formData = new FormData()
    formData.append('challengeId', challengeId)
    frames.forEach((f, i) => formData.append('frames', f, `frame-${i}.jpg`))
    const res = await apiClient.post<{ success: boolean; data: { status: string; selfieUrl: string } }>('/api/user/kyc/liveness', formData)
    return res.data.data
  },

  async uploadAvatar(file: File): Promise<{ success: boolean; url: string }> {
    const formData = new FormData()
    formData.append('file', file)
    const res = await apiClient.post<{ success: boolean; url: string }>('/api/upload', formData)
    return res.data
  },

  async getPayoutMethods(): Promise<{ success: boolean; data: PayoutMethod[] }> {
    const res = await apiClient.get<{ success: boolean; data: PayoutMethod[] }>('/api/user/payout-methods')
    return res.data
  },

  async addPayoutMethod(data: {
    bankCode: string
    accountNumber: string
    accountHolderName: string
    inn?: string
    isDefault?: boolean
  }): Promise<{ success: boolean; data: PayoutMethod }> {
    const res = await apiClient.post<{ success: boolean; data: PayoutMethod }>('/api/user/payout-methods', data)
    return res.data
  },

  async deletePayoutMethod(id: string): Promise<{ success: boolean }> {
    const res = await apiClient.delete<{ success: boolean }>(`/api/user/payout-methods/${id}`)
    return res.data
  },

  async getPayouts(page = 1, perPage = 20): Promise<{ success: boolean; data: PayoutInstruction[]; balance?: PayoutBalance; meta?: PageMeta }> {
    const res = await apiClient.get<{ success: boolean; data: PayoutInstruction[]; balance?: PayoutBalance; meta?: PageMeta }>('/api/user/payouts', { page, perPage })
    return res.data
  },

  // Manual escrow settlement — the buyer side of a won lot.
  async getPurchases(): Promise<{ success: boolean; data: Purchase[] }> {
    const res = await apiClient.get<{ success: boolean; data: Purchase[] }>('/api/user/purchases')
    return res.data
  },

  async claimPayment(auctionId: string, body: { bankId?: string; reference: string; note?: string }): Promise<{ success: boolean; data: Purchase }> {
    const res = await apiClient.post<{ success: boolean; data: Purchase }>(`/api/auctions/${auctionId}/payment-claim`, body)
    return res.data
  },

  async saveReview(auctionId: string, rating: number, comment: string): Promise<{ success: boolean; data: OwnReview | null }> {
    const res = await apiClient.post<{ success: boolean; data: OwnReview | null }>('/api/user/reviews', { auctionId, rating, comment })
    return res.data
  },

  async getLotSellerReviews(auctionId: string, page = 1): Promise<{ success: boolean; data: { summary: { rating: number | null; reviewCount: number }; reviews: PublicSellerReview[] } }> {
    const res = await apiClient.get<any>(`/api/auctions/${auctionId}/seller-reviews`, { page })
    return res.data
  },

  async confirmDelivery(auctionId: string): Promise<{ success: boolean; data: Purchase | null }> {
    const res = await apiClient.post<{ success: boolean; data: Purchase | null }>(`/api/auctions/${auctionId}/confirm-delivery`, {})
    return res.data
  },

  // A resolved group buy's settlement (see groupBuySettlementModel.ts) — same
  // shape as a regular Purchase, but addressed by payment id since one lot's
  // group buy can settle into many participants' payments.
  async getGroupBuyPurchases(): Promise<{ success: boolean; data: Purchase[] }> {
    const res = await apiClient.get<{ success: boolean; data: Purchase[] }>('/api/user/group-buy-purchases')
    return res.data
  },

  async claimGroupBuyPayment(paymentId: string, body: { bankId?: string; reference: string; note?: string }): Promise<{ success: boolean; data: Purchase }> {
    const res = await apiClient.post<{ success: boolean; data: Purchase }>(`/api/group-buys/participants/${paymentId}/payment-claim`, body)
    return res.data
  },

  async confirmGroupBuyDelivery(paymentId: string): Promise<{ success: boolean; data: Purchase | null }> {
    const res = await apiClient.post<{ success: boolean; data: Purchase | null }>(`/api/group-buys/participants/${paymentId}/confirm-delivery`, {})
    return res.data
  },

  async requestPayout(amount: number, payoutMethodId?: string): Promise<{ success: boolean; data: PayoutInstruction }> {
    const res = await apiClient.post<{ success: boolean; data: PayoutInstruction }>('/api/user/payouts', {
      amount,
      payoutMethodId,
    })
    return res.data
  },

  async getSettings(): Promise<{ success: boolean; data: any }> {
    const res = await apiClient.get<{ success: boolean; data: any }>('/api/user/settings')
    return res.data
  },

  async updateSettings(settings: any): Promise<{ success: boolean; data: any }> {
    const res = await apiClient.put<{ success: boolean; data: any }>('/api/user/settings', settings)
    return res.data
  },

  async getDisputes(status?: string, page = 1, perPage = 20): Promise<{ success: boolean; data: any[]; meta?: PageMeta }> {
    const res = await apiClient.get<{ success: boolean; data: any[]; meta?: PageMeta }>('/api/user/disputes', { status, page, perPage })
    return res.data
  },

  async createDispute(data: {
    auctionId: string
    reason?: string
    reasonCategory?: string
    details?: string
    role?: 'buyer' | 'seller'
    respondentId?: string
  }): Promise<{ success: boolean; data: any; message?: string }> {
    const res = await apiClient.post<{ success: boolean; data: any; message?: string }>('/api/user/disputes', data)
    return res.data
  },
}

export default userService

